import React, { useState } from 'react';

const fmt = (val) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(val || 0);

const calculateNet = (deal) => {
  const contractPrice = parseFloat(deal.contractPrice) || 0;
  const commissionPercent = parseFloat(deal.commissionPercent) || 0;
  const commission = contractPrice * (commissionPercent / 100);
  const brokerSplitValue = parseFloat(deal.brokerSplitValue) || 0;
  const brokerSplit = deal.brokerSplitType === 'flat'
    ? brokerSplitValue
    : commission * (brokerSplitValue / 100);
  const transactionFee = parseFloat(deal.transactionFee) || 0;
  const tcFee = parseFloat(deal.tcFee) || 0;
  return commission - brokerSplit - transactionFee - tcFee;
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function PipelineFinancials({ activeDeals, closedDeals }) {
  // Use a narrower set of paddings on mobile so the dark "Earnings" card
  // and the icons inside it (🎯 / 🔥 / 📊 / ✅) don't sit flush to the edge.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [period, setPeriod] = useState('mtd');
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const filteredClosedDeals = (closedDeals || []).filter(deal => {
    if (!deal.closedAt) return false;
    const closed = deal.closedAt.toDate ? deal.closedAt.toDate() : new Date(deal.closedAt);
    if (period === 'mtd') return closed.getMonth() === currentMonth && closed.getFullYear() === currentYear;
    if (period === 'ytd') return closed.getFullYear() === currentYear;
    return closed.getMonth() === selectedMonth && closed.getFullYear() === currentYear;
  });

  const getProjectedEarnings = () => {
    if (!activeDeals || activeDeals.length === 0) return 0;
    return activeDeals.reduce((sum, deal) => {
      const net = calculateNet(deal);
      const probability = (deal.closeProbability || 5) / 100;
      return sum + (net * probability);
    }, 0);
  };

  const actual = filteredClosedDeals.reduce((sum, deal) => sum + calculateNet(deal), 0);
  const projected = getProjectedEarnings();
  const totalPipeline = (activeDeals || []).reduce((sum, deal) => sum + calculateNet(deal), 0);
  const fillPct = totalPipeline > 0 ? Math.min(100, Math.round((actual / totalPipeline) * 100)) : 0;

  const periodLabel = period === 'mtd'
    ? `${MONTHS[currentMonth]} ${currentYear}`
    : period === 'ytd'
    ? `Full Year ${currentYear}`
    : `${MONTHS[selectedMonth]} ${currentYear}`;

  const closedCount = filteredClosedDeals.length;

  // Mobile-aware overrides for the dark Earnings card.
  const wrapStyle = isMobile
    ? { ...s.wrap, padding: '20px 16px', borderRadius: 16 }
    : s.wrap;
  const heroCardStyle = isMobile
    ? { ...s.heroCard, padding: '0 0 20px 0', flex: '1 1 100%', minWidth: 0 }
    : s.heroCard;
  const statCardStyle = isMobile
    ? { ...s.statCard, padding: '12px 0', minWidth: 0, flex: '1 1 45%' }
    : s.statCard;
  const dividerStyle = isMobile ? { display: 'none' } : s.divider;

  return (
    <div style={wrapStyle}>

      {/* Header Row */}
      <div style={s.headerRow}>
        <div>
          <div style={s.headerTitle}>💰 Earnings </div>
          <div style={s.headerSub}>{periodLabel}</div>
        </div>
        <div style={s.periodBtns}>
          {['mtd', 'ytd', 'month'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{ ...s.periodBtn, ...(period === p ? s.periodBtnActive : {}) }}
            >
              {p === 'mtd' ? 'This Month' : p === 'ytd' ? 'This Year' : 'Pick Month'}
            </button>
          ))}
          {period === 'month' && (
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(parseInt(e.target.value))}
              style={s.monthSelect}
            >
              {MONTHS.map((m, i) => (
                <option key={i} value={i}>{m} {currentYear}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Main Stats */}
      <div style={isMobile ? { ...s.statsRow, gap: 16 } : s.statsRow}>

        {/* Actual Earnings — hero stat */}
        <div style={heroCardStyle}>
          <div style={s.heroGlow} />
          <div style={s.heroLabel}>✅ Actual Earnings</div>
          <div style={s.heroValue}>{fmt(actual)}</div>
          <div style={s.heroSub}>{closedCount} deal{closedCount !== 1 ? 's' : ''} closed</div>
          {/* Progress bar toward pipeline total */}
          <div style={s.progressWrap}>
            <div style={s.progressBg}>
              <div style={{ ...s.progressFill, width: `${fillPct}%` }} />
            </div>
            <span style={s.progressLabel}>{fillPct}% of pipeline earned</span>
          </div>
        </div>

        {/* Divider */}
        <div style={dividerStyle} />

        {/* Projected */}
        <div style={statCardStyle}>
          <div style={s.statIcon}>🎯</div>
          <div style={s.statLabel}>Projected Earnings</div>
          <div style={{ ...s.statValue, color: '#60a5fa' }}>{fmt(projected)}</div>
          <div style={s.statSub}>Weighted by close probability</div>
        </div>

        <div style={dividerStyle} />

        {/* Full Pipeline Value */}
        <div style={statCardStyle}>
          <div style={s.statIcon}>📊</div>
          <div style={s.statLabel}>Pipeline Value</div>
          <div style={{ ...s.statValue, color: '#a78bfa' }}>{fmt(totalPipeline)}</div>
          <div style={s.statSub}>If all deals close</div>
        </div>

        <div style={dividerStyle} />

        {/* Active Deals */}
        <div style={statCardStyle}>
          <div style={s.statIcon}>🔥</div>
          <div style={s.statLabel}>Active Deals</div>
          <div style={{ ...s.statValue, color: '#fb923c' }}>{activeDeals?.length || 0}</div>
          <div style={s.statSub}>In pipeline right now</div>
        </div>

      </div>
    </div>
  );
}

const s = {
  wrap: {
    background: 'linear-gradient(135deg, #071d39 0%, #0d1f3c 100%)',
    borderRadius: 20,
    padding: '28px 32px',
    marginBottom: 24,
    border: '1px solid #1e2f52',
    position: 'relative',
    overflow: 'hidden',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: '#e8f0fe',
    letterSpacing: '-0.3px',
  },
  headerSub: {
    fontSize: 12,
    color: 'rgba(232,240,254,0.35)',
    marginTop: 2,
  },
  periodBtns: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  periodBtn: {
    padding: '6px 16px',
    borderRadius: 20,
    border: '1px solid #1e2f52',
    background: 'transparent',
    color: 'rgba(232,240,254,0.4)',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  periodBtnActive: {
    background: 'rgba(126,255,160,0.12)',
    border: '1px solid rgba(126,255,160,0.3)',
    color: '#7effa0',
    fontWeight: 700,
  },
  monthSelect: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid #1e2f52',
    background: '#132040',
    color: '#e8f0fe',
    fontSize: 12,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  statsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flexWrap: 'wrap',
  },
  heroCard: {
    flex: '1.5',
    minWidth: 200,
    padding: '0 32px 0 0',
    position: 'relative',
  },
  heroGlow: {
    position: 'absolute',
    top: -20,
    left: -20,
    width: 120,
    height: 120,
    background: 'radial-gradient(circle, rgba(117,187,46,0.15) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: 'rgba(232,240,254,0.45)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: 8,
  },
  heroValue: {
    fontSize: 48,
    fontWeight: 800,
    color: '#75BB2E',
    letterSpacing: '-1px',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
    marginBottom: 6,
  },
  heroSub: {
    fontSize: 12,
    color: 'rgba(232,240,254,0.35)',
    marginBottom: 16,
  },
  progressWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  progressBg: {
    height: 6,
    background: 'rgba(255,255,255,0.08)',
    borderRadius: 3,
    overflow: 'hidden',
    width: '100%',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #75BB2E, #a3e635)',
    borderRadius: 3,
    transition: 'width 0.5s ease',
  },
  progressLabel: {
    fontSize: 10,
    color: 'rgba(232,240,254,0.3)',
  },
  divider: {
    width: 1,
    height: 90,
    background: '#1e2f52',
    flexShrink: 0,
    margin: '0 8px',
  },
  statCard: {
    flex: 1,
    minWidth: 130,
    padding: '0 24px',
  },
  statIcon: {
    fontSize: 22,
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(232,240,254,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: 6,
  },
  statValue: {
    fontSize: 30,
    fontWeight: 800,
    letterSpacing: '-0.5px',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
    marginBottom: 4,
  },
  statSub: {
    fontSize: 11,
    color: 'rgba(232,240,254,0.28)',
    fontStyle: 'italic',
  },
};